import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {finalize, forkJoin, map, of, switchMap, tap} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ProgressService} from '../../../shared/services/progress.service';
import {SnackbarService} from '../../../shared/services/snackbar.service';
import {SettingsDataService} from '../../../shared/services/settings-data.service';
import {ConfigRouteEnum} from '../../../shared/enums/config-route.enum';
import {ConfigOverviewModel} from '../../../shared/components/models/config-overview.model';
import {ConfigCardModel} from '../../../shared/models/config-card.model';
import {TranslationOverview} from "../models/translation.model";
import {Router} from "@angular/router";
import {ConfigTableDisplayedColumns} from "../../../shared/components/models/config-table.model";
import {ConfigOverviewComponent} from "../../../shared/components/config-overview/config-overview.component";
import {
    ConfigAmountBadgesComponent
} from "../../../shared/components/config-amount-badges/config-amount-badges.component";
import {RefreshService} from "../../../shared/services/refresh.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {UiSettingsService} from "../../../shared/services/ui-settings.service";

@Component({
    selector: 'du-translation-overview',
    templateUrl: './translation-overview.component.html',
    styleUrls: ['./translation-overview.component.scss'],
    standalone: true,
    imports: [
        ConfigOverviewComponent,
    ],
})
export class TranslationOverviewComponent implements OnInit {
    translationOverviewModel?: ConfigOverviewModel;

    private readonly _router: Router = inject(Router);

    private allCards: ConfigCardModel[] = [];
    private displayedColumns: any[] = [];

    private products: TranslationOverview[] = [];

    private readonly settingsService = inject(SettingsDataService);
    private readonly uiSettings = inject(UiSettingsService);
    private readonly progress = inject(ProgressService);
    private readonly snackBar = inject(SnackbarService);

    protected readonly ConfigRouteEnum = ConfigRouteEnum;
    private readonly translate = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly refreshService = inject(RefreshService);

    ngOnInit(): void {
        this.loadTranslations();

        this.refreshService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.loadTranslations();
        });

    }

    private loadTranslations(): void {
        this.progress.startLoading();

        this.uiSettings.translationApps$
            .pipe(
                switchMap((products: TranslationOverview[]) => {
                    if (products.length === 0) {
                        return of([]);
                    }

                    const requests = products.map(product =>
                        this.settingsService.findAllByApplicationName(product.searchName).pipe(
                            map(entries => ({...product, amount: entries.length})),
                            catchError(() => of({...product, amount: 0}))
                        )
                    );

                    return forkJoin(requests);
                }),
                tap((products) => {
                    this.products = products;
                    this.buildOverview();
                }),
                catchError(() => {
                    this.snackBar.Error(this.translate.instant("snackbar.translation.error"));
                    this.products = [];
                    this.buildOverview();
                    return of([]);
                }),
                finalize(() => this.progress.stopLoading())
            )
            .subscribe();
    }

    private buildOverview(): void {
        this.allCards = this.products.map(
            (t, id) =>
                new ConfigCardModel({
                    id: id,
                    title: t.name,
                    searchName: t.searchName,
                    body: null,
                    logoUrl: null,
                    route: {
                        commands: [ConfigRouteEnum.TRANSLATIONS, t.searchName],
                    },
                    badges: [
                        {amount: (t.amount ?? 0).toString(), label: 'translation.translations', routerLink: undefined}
                    ],
                })
        );

        this.allCards.forEach(card => {
            (card as any).ConfigBadgesComponent = {badges: card.badges, direction: 'row'};
        });

        this.displayedColumns = ConfigTableDisplayedColumns.build(
            {HeaderCellName: 'translation.applications', PropertyName: 'title'},
            {HeaderCellName: '', PropertyName: 'ConfigBadgesComponent', Component: ConfigAmountBadgesComponent},
        );

        this.translationOverviewModel = {
            title: 'translation.translations',
            buttons: null,
            cards: this.allCards,
            table: {
                settings: {
                    header: true,
                    headerAction: true,
                    search: true,
                },
                data: this.allCards,
                displayedColumns: this.displayedColumns,
                onRowClick: (row: any) => this._router.navigate(row.route.commands, row.route.extras),
            },
        };
    }
}
